/**
 * SideMenuTheme - Light Eye-Friendly Theme
 * 
 * Soft design with:
 * - Clean white backgrounds
 * - Subtle shadows
 * - Light gray borders
 */

import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';

export const openedMixin = (theme, drawerwidth) => ({
  width: drawerwidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

export const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 20px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 20px)`,
  },
});

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => !['open', 'sidemenutheme', 'drawerwidth', 'appbarTheme'].includes(prop),
})(({ theme, open, drawerwidth, appbarTheme }) => ({
  zIndex: 1100,
  transition: theme.transitions.create(['width', 'margin', 'background'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-End',
  height: '70px',
  // Light clean background
  background: '#FFFFFF',
  // Subtle shadow
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
  // Light border
  borderBottom: '1px solid #E2E8F0',
}));

export const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => !['open', 'sidemenutheme', 'drawerwidth'].includes(prop),
})(({ theme, open, sidemenutheme, drawerwidth }) => ({
  width: drawerwidth,
  height: '100%',
  display: 'flex !important',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: '0px',
  position: 'relative',
  '& .MuiPaper-root': {
    padding: '16px 0px',
    // height: 'calc(100vh - 130px)',
    paddingBottom: '60px',
    top: '70px',
    margin: 'auto 0 !important',
    // Light gradient background
    background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
    // Subtle shadow
    boxShadow: '2px 0 15px rgba(0, 0, 0, 0.04)',
    // Light border
    borderRight: '1px solid #E2E8F0',
    opacity: 1,
    // Smooth scrollbar
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#F1F5F9',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#CBD5E1',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#94A3B8',
    },
  },
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme, drawerwidth),
    '& .MuiDrawer-paper': openedMixin(theme, drawerwidth),
  }),
  ...(!open && {
    ...closedMixin(theme, drawerwidth),
    '& .MuiDrawer-paper': closedMixin(theme, drawerwidth),
  }),
}));